import { Autocomplete, MenuItem } from '@mui/material';
import { SetStateAction, useContext, useEffect, useState } from 'react';
import { useAccount } from '@/account/useAccount';
import { AppConfig, useAppConfigContext } from '@/context/AppConfig';
import { AppConstants, useAppConstantsContext } from '@/context/AppConstants';
import { useUnclaimedMoneyMultiStateSearch } from '@/hooks/useUnclaimedMoneyMultiStateSearch';
import { useUnclaimedMoneySearch } from '@/hooks/useUnclaimedMoneySearch';
import { useSriracha } from '@/notifications/index';
import {
  Button,
  Input,
  Text,
  Select,
  Grid,
  Checkbox,
  styled,
  useTheme,
} from '@/theme/index';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { useUnclaimedMoneyStatesOptionsAbbr } from '@/utils/unclaimedMoneyStates';
import { useStatesOptionsAbbr } from '../../utils/states';
import type { FormEventHandler } from 'react';

const StyledInput = styled(Input)(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
}));
interface OptionType {
  value: string;
  display: string;
  url: string;
}

interface SearchUnclaimedMoneyFormProps {
  fname?: string;
  lname?: string;
  state?: string;
  fromIframePage?: boolean;
  stateList?: string[];
  fromDashboard?: boolean;
}

export function SearchUnclaimedMoneyForm({
  fname,
  lname,
  state,
  fromIframePage,
}: SearchUnclaimedMoneyFormProps) {
  const { trackEvent, trackEventGA4 } = useAppConfigContext();
  const allStates = useStatesOptionsAbbr();
  const { redirect } = useRedirect();
  const states = allStates.filter(
    (state) => state.value !== 'All' && state.display !== 'All'
  );
  const [fNameInput, setFNameInput] = useState(fname || '');
  const [lNameInput, setLNameInput] = useState(lname || '');
  const [stateInput, setStateInput] = useState(state || '');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [stateError, setStateError] = useState('');

  const {
    config: { firstUnclaimedMoneyNotification: firstUnclaimedMoneyReminder },
  } = useAppConstantsContext();

  const { sendNotification, getSuggestedReportNotificationSources } =
    useSriracha();

  const { data: notification_sources } = getSuggestedReportNotificationSources;

  const { data: account } = useAccount(true);

  function onStateSelect(value: string) {
    setStateInput(value);
  }

  const onSearchUnclaimedMoney: FormEventHandler<HTMLFormElement> = async (
    e
  ) => {
    e.preventDefault();

    trackEventGA4({
      event_name: 'report_search',
      report_type: 'unclaimed_money',
      search_by: 'unclaimed_money',
      search_from: `unclaimed_money_search`,
    });

    if (!lNameInput || !fNameInput || !stateInput) {
      setFirstNameError(!fNameInput ? 'This field is required' : '');
      setLastNameError(!lNameInput ? 'This field is required' : '');
      setStateError(!stateInput ? 'This field is required' : '');
      return;
    }

    const UnclaimedMoneySearchParams = {
      fName: fNameInput,
      lName: lNameInput,
      state: stateInput,
    };

    const notificationSources = notification_sources?.suggested_report_sources;

    const firstUnclaimedMoneyNotification = notificationSources?.find(
      (item: string) => item === 'first_unclaimed_money_reminder'
    );

    if (!firstUnclaimedMoneyNotification && firstUnclaimedMoneyReminder) {
      sendNotification.mutate({
        'notification[user_code]': account?.account.user_info.user_code,
        'notification[notification_type]': 'first_unclaimed_money_reminder',
        'notification[data][first_name]': fNameInput,
        'notification[data][last_name]': lNameInput,
        'notification[data][state]': stateInput,
      });
    }

    redirect(
      `/search/unclaimed?fname=${UnclaimedMoneySearchParams.fName}&lname=${UnclaimedMoneySearchParams.lName}&state=${UnclaimedMoneySearchParams.state}`
    );

    if (fromIframePage) {
      trackEvent(
        'unclaimed money search',
        'engagement click',
        'unclaimed Money Results'
      );
      window.location.reload();
    }
  };

  return (
    <form
      id="search-unclaimed-money"
      onSubmit={onSearchUnclaimedMoney}
      autoComplete="off"
    >
      <Grid
        container
        direction="row"
        spacing={2}
        py={2.5}
        alignItems={
          firstNameError || lastNameError || stateError ? 'center' : 'flex-end'
        }
      >
        <Grid item width={{ xs: '100%', md: '30%' }}>
          <Input
            fullWidth
            id="fName"
            type="text"
            name="fName"
            placeholder="First Name"
            onChange={(e: { target: { value: SetStateAction<string> } }) => {
              setFNameInput(e.target.value);
            }}
            value={fNameInput}
            error={Boolean(firstNameError)}
          />
          {firstNameError && (
            <Text color="error" variant="caption">
              {firstNameError}
            </Text>
          )}
        </Grid>
        <Grid item width={{ xs: '100%', md: '30%' }}>
          <Input
            fullWidth
            id="lName"
            type="text"
            name="lName"
            placeholder="Last Name"
            onChange={(e: { target: { value: SetStateAction<string> } }) => {
              setLNameInput(e.target.value);
            }}
            value={lNameInput}
            error={Boolean(lastNameError)}
          />
          {lastNameError && (
            <Text color="error" variant="caption">
              {lastNameError}
            </Text>
          )}
        </Grid>
        <Grid item width={{ xs: '100%', md: '20%' }}>
          <Select
            options={states}
            onOptionSelect={onStateSelect}
            id="state"
            error={Boolean(stateError)}
            value={stateInput}
            placeholder="State"
          />
          {stateError && (
            <Text color="error" variant="caption">
              {stateError}
            </Text>
          )}
        </Grid>
        <Grid item width={{ xs: '100%', md: '20%' }}>
          <Button type="submit" variant="contained" color="success" fullWidth>
            Search
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export function SearchNewUnclaimedMoneyForm({
  fname,
  lname,
  stateList,
}: SearchUnclaimedMoneyFormProps) {
  const theme = useTheme();
  const { trackEventGA4 } = useContext(AppConfig);
  const allStates = useUnclaimedMoneyStatesOptionsAbbr();
  const searchUnclaimedMoney = useUnclaimedMoneySearch(false);
  const searchUnclaimedMoneyMultiState = useUnclaimedMoneyMultiStateSearch();
  const states = allStates.filter(
    (state) => state.value !== 'All' && state.display !== 'All'
  );

  const [fNameInput, setFNameInput] = useState(fname || '');
  const [lNameInput, setLNameInput] = useState(lname || '');
  const [stateInput, setStateInput] = useState<OptionType[]>([]);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [stateError, setStateError] = useState('');

  const {
    config: { firstUnclaimedMoneyNotification: firstUnclaimedMoneyReminder },
  } = useContext(AppConstants);

  const { sendNotification, getSuggestedReportNotificationSources } =
    useSriracha();

  const { data: notification_sources } = getSuggestedReportNotificationSources;

  const { data: account } = useAccount(true);

  const onStateSelect = (event: unknown, value: OptionType[]) => {
    if (value.length > 5) return;

    setStateInput(value);
  };

  const onSearchUnclaimedMoney: FormEventHandler<HTMLFormElement> = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    trackEventGA4({
      event_name: 'report_search',
      report_type: 'unclaimed_money',
      search_by: 'unclaimed_money',
      search_from: `unclaimed_money_search`,
    });

    if (!lNameInput || !fNameInput || stateInput.length === 0) {
      setFirstNameError(!fNameInput ? 'This field is required' : '');
      setLastNameError(!lNameInput ? 'This field is required' : '');
      setStateError(stateInput.length === 0 ? 'This field is required' : '');
      return;
    }

    const UnclaimedMoneySearchParams = {
      fName: fNameInput,
      lName: lNameInput,
      state: stateInput,
    };

    const notificationSources = notification_sources?.suggested_report_sources;

    const firstUnclaimedMoneyNotification = notificationSources?.find(
      (item: string) => item === 'first_unclaimed_money_reminder'
    );

    const statesSelected = UnclaimedMoneySearchParams.state.map(
      (state) => state.value
    );

    if (!firstUnclaimedMoneyNotification && firstUnclaimedMoneyReminder) {
      sendNotification.mutate({
        'notification[user_code]': account?.account.user_info.user_code,
        'notification[notification_type]': 'first_unclaimed_money_reminder',
        'notification[data][first_name]': fNameInput,
        'notification[data][last_name]': lNameInput,
        'notification[data][state]': statesSelected[0],
      });
    }

    if (UnclaimedMoneySearchParams.state.length > 1) {
      searchUnclaimedMoneyMultiState.mutate({
        first_name: UnclaimedMoneySearchParams.fName,
        last_name: UnclaimedMoneySearchParams.lName,
        states: statesSelected,
      });
    } else {
      searchUnclaimedMoney.mutate({
        first_name: UnclaimedMoneySearchParams.fName,
        last_name: UnclaimedMoneySearchParams.lName,
        state: statesSelected[0],
      });
    }
  };

  useEffect(() => {
    if (stateList?.length) {
      const statesSelected = stateList
        .map((state) => states.find((s) => s.value === state))
        .filter((state): state is OptionType => state !== undefined);
      setStateInput(statesSelected);
    }
  }, [stateList]);

  return (
    <form
      id="search-unclaimed-money"
      onSubmit={onSearchUnclaimedMoney}
      autoComplete="off"
    >
      <Grid container direction="row" spacing={2} py={2.5} alignItems={'start'}>
        <Grid item width={{ xs: '100%', sm: '100%', md: '20%' }}>
          <StyledInput
            fullWidth
            id="fName"
            type="text"
            name="fName"
            placeholder="First Name"
            onChange={(e: { target: { value: SetStateAction<string> } }) => {
              setFNameInput(e.target.value);
            }}
            value={fNameInput}
            error={Boolean(firstNameError)}
          />
          {firstNameError && (
            <Text color="error" variant="caption">
              {firstNameError}
            </Text>
          )}
        </Grid>
        <Grid item width={{ xs: '100%', sm: '100%', md: '20%' }}>
          <StyledInput
            fullWidth
            id="lName"
            type="text"
            name="lName"
            placeholder="Last Name"
            onChange={(e: { target: { value: SetStateAction<string> } }) => {
              setLNameInput(e.target.value);
            }}
            value={lNameInput}
            error={Boolean(lastNameError)}
          />
          {lastNameError && (
            <Text color="error" variant="caption">
              {lastNameError}
            </Text>
          )}
        </Grid>
        <Grid item width={{ xs: '100%', sm: '100%', md: '45%' }}>
          <Autocomplete
            sx={{
              width: '100%',
              '.MuiAutocomplete-input': {
                paddingTop: '4px !important',
              },
              '&.MuiAutocomplete-hasClearIcon': {
                '& > div': {
                  '& > div.MuiInputBase-root': {
                    minHeight: 40,
                    height: 'fit-content',
                  },
                },
              },
              '.MuiAutocomplete-endAdornment': {
                height: 'auto',
              },
              '.MuiAutocomplete-tag': {
                height: '27px',
              },
              '.MuiChip-root': {
                margin: '0px',
              },
              '.MuiChip-label': {
                paddingLeft: '6px',
              },
            }}
            multiple
            options={states}
            value={stateInput}
            onChange={onStateSelect}
            getOptionLabel={(option) => option.display}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            disableCloseOnSelect
            renderInput={(params) => (
              <>
                <Input
                  {...params}
                  variant="outlined"
                  placeholder={
                    stateInput.length === 0 ? 'State (Select up to 5)' : ''
                  }
                  error={Boolean(stateError)}
                />
                {stateError && (
                  <Text color="error" variant="caption">
                    {stateError}
                  </Text>
                )}
              </>
            )}
            renderOption={(props, option, { selected }) => (
              <MenuItem
                {...props}
                key={option.value}
                value={option.value}
                sx={{
                  justifyContent: 'space-between',
                  color: theme.palette.text.primary,
                }}
              >
                <Checkbox checked={selected} color="primary" />
                {option.display}
              </MenuItem>
            )}
            data-cy="states_input"
          />
        </Grid>
        <Grid item width={{ xs: '100%', sm: '100%', md: '15%' }}>
          <Button type="submit" variant="contained" color="success" fullWidth>
            Search
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
